import React, { ReactNode } from "react";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";

import cn from "classnames";
import { WrapperComponent } from "../_shared-components/arrangement/WrapperComponent";
import { QuotationStoryType } from "@swo/storefront";
import { createJpgLink } from "../_shared-components/image-related/createJpgAndWebpLinks";
import { FONTS, fontSize } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { plainTextMdStyles, plainTextSmPlusStyles } from "../../styles/text-styles";

export function QuotationStoryComponent(props: QuotationStoryType) {
  const withImage = !!props.backgroundImage;

  const transformedBackgroundImageUrl = props.backgroundImage?.url
    ? createJpgLink(props.backgroundImage?.url, props.width === "full" ? 2000 : 1400)
    : undefined;

  const width = props.width == "full" ? "full" : "content-but-full-on-small";

  return (
    <WrapperComponent width={width}>
      <div
        className={cn(
          withImage ? "with-image" : undefined,
          `color-${props.textColor}`,
          getComponentCMSClassname(props?.sys?.id),
        )}
        css={css`
          &.color-black {
            color: black;
            background-color: ${CSS_COLORS.White};
          }

          &.color-white {
            color: ${CSS_COLORS.White};
          }

          &.with-image {
            background: url(${transformedBackgroundImageUrl}) no-repeat center center;
            background-size: cover;

            padding: 80px 0;
            ${CSS_MQ.atLeastSm} {
              padding: 110px 0;
            }
            ${CSS_MQ.atLeastMd} {
              padding: 150px 0;
            }
            ${CSS_MQ.atLeastLg} {
              padding: 180px 0;
            }
          }
        `}
      >
        <QuotationInternalContent author={props.author}>{props.text}</QuotationInternalContent>
      </div>
    </WrapperComponent>
  );
}

const QuotationInternalContent: React.FC<
  React.PropsWithChildren<{
    author: React.ReactNode;
    className?: string;
  }>
> = props => {
  return (
    <div css={wrapperStyles} className={props.className}>
      <span css={textStyles}>{props.children}</span>

      {props.author && <span css={authorStyles}>{props.author}</span>}
    </div>
  );
};

export const QuotationStoryBlock: React.FC<
  React.PropsWithChildren<{
    author: React.ReactNode;
    className?: string;
  }>
> = props => {
  return (
    <QuotationInternalContent className={props.className} author={props.author}>
      {props.children}
    </QuotationInternalContent>
  );
};

const authorStyles = [
  FONTS.SwarovskiFutura500,
  fontSize.x2,
  css`
    margin-top: 20px;

    color: inherit;

    text-transform: uppercase;
    display: block;

    &:before {
      content: "— ";
    }

    > * {
      display: inline !important;
    }
  `,
];

const textStyles = css`
  ${FONTS.SwarovskiFutura700};
  color: inherit;
  text-transform: uppercase;

  ${plainTextSmPlusStyles};

  ${CSS_MQ.atLeastSm} {
    ${plainTextMdStyles};
  }

  ${CSS_MQ.atLeastLg} {
    font-size: 30px;
    letter-spacing: 1.5px;
    line-height: 32px;
  }
`;

const wrapperStyles = css`
  border-top: 1px solid ${CSS_COLORS.Grey2};
  border-bottom: 1px solid ${CSS_COLORS.Grey2};

  .with-image.color-black & {
    border-top: 1px solid black;
    border-bottom: 1px solid black;
    backdrop-filter: brightness(1.3);
  }
  .with-image.color-white & {
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    backdrop-filter: brightness(0.7);
  }

  .with-image & {
    padding-left: 8px;
    padding-right: 8px;
  }

  width: 100%;
  ${CSS_MQ.atLeastSm} {
    width: 28em;
  }
  ${CSS_MQ.atLeastLg} {
    width: 26em;
  }
  max-width: var(--max-content-width-new);

  padding-top: 20px;
  padding-bottom: 20px;
  ${CSS_MQ.atLeastMd} {
    padding-top: 40px;
    padding-bottom: 40px;
  }

  margin-left: auto;
  margin-right: auto;

  text-align: center;

  ${textStyles};
`;
